import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, message, Table, Pagination, Dropdown, Tooltip, Button } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined, LoadingOutlined, CheckOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import ChangeLogButton from '../changeLog/ChangeLogButton';
import ExportButton from '../../../components/common/ExportButton';
import FilterButton from '../../../components/common/FilterButton';
import ExportPanel from '../../../components/common/ExportPanel';
import type { ColumnsType } from 'antd/es/table';
import EditIcon from '../../../assets/icons/ic-edit.svg?react';
import TrashIcon from '../../../assets/icons/ic-trash.svg?react';
import ButtonGroup from '../../../components/common/ButtonGroup';
import ConfirmModal from '../../../components/common/ConfirmModal';
import AddItemButton from '../../../components/common/AddItemButton';
import RightContentDrawer from '../../../components/common/RightContentDrawer';
import SortableColumnHeader from '../../../components/common/SortableColumnHeader';
import GapsFilterDrawer, { GapsFilterState } from './GapsFilterDrawer';
import { useTableSort } from '../../../hooks';
import { RootState, AppDispatch } from '../../../store';
import {
  getAllGaps,
  Gap,
  deleteGap,
  updateGap,
  UpdateGapRequest,
  mapStatusToApi,
  mapGapTypeToApi,
  mapPriorityToApi,
  GapStatus,
  GapObjective
} from '../../../services/gapService';
import {
  setGaps,
  setCurrentPage,
  setPageSize as setPageSizeAction,
} from '../../../store/slices/gapsSlice';
import { clearHierarchyData } from '../../../store/slices/hierarchySlice';
import { setGlobalPageSize } from '../../../store/slices/generalSlice';
import warningIcon from '../../../assets/icons/ic-warning.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import RiskMatrixModal from '../ex4/tab2/RiskMatrixModal';
import { mapRiskRankingToApi, RiskRanking } from '../../../services/types';

const { Title } = Typography;


// ==================== STYLED COMPONENTS ====================

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
`;

const PageHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;


const HeaderButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TableWrapper = styled.div`
  position: relative;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  overflow: hidden;
  
  .ant-table-wrapper {
    overflow-x: auto;
    max-width: 100%;
  }
  
  .ant-table {
    font-size: 14px;
    border: none;
  }
  
  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    color: #6B7280;
    font-size: 14px;
    padding: 16px 12px;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    white-space: nowrap;
    vertical-align: middle;
  
    &:last-child {
      border-right: none;
    }

    &::before {
      display: none !important;
    }

    /* Allow text wrap for specific long headers */
    &.wrap-header {
      white-space: normal;
      line-height: 1.4;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 20px 12px;
    font-size: 14px;
    color: #1F2937;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    vertical-align: middle;
    background: white;
  
    &:last-child {
      border-right: none;
    }
  }
  
  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
  
  /* Remove selection highlight */
  .ant-table-tbody > tr.ant-table-row-selected > td,
  .ant-table-tbody > tr.ant-table-row:active > td {
    background: transparent !important;
  }

  /* Sticky column styles */
  .ant-table-cell-fix-left {
    background: white !important;
    z-index: 2;
  }

  .ant-table-thead > tr > th.ant-table-cell-fix-left {
    background: #F8FAFC !important;
  }

  .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left {
    background: #F9FAFB !important;
  }
`;

const PaginationContainer = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: flex-start;
  border-top: 1px solid #E5E7EB;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 6px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
`;

const EmptyStateMessage = styled.p`
  color: #6B7280;
  font-size: 16px;
  margin-bottom: 24px;
`;

// ==================== CHIP COMPONENTS ====================

const StatusChip = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: ${props => {
    switch (props.$status) {
      case 'Open': return '#4AB7B6';
      case 'Draft': return '#E5B454';
      case 'Archived': return '#9CA3AF';
      default: return '#F3F4F6';
    }
  }};
  color: white;
  
  &::before {
    content: '●';
    font-size: 10px;
  }
`;

const GapIdLink = styled.span`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
`;

const CycleChip = styled.span`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #ECFDF5;
  color: #047857;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ProcessChip = styled.span`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  
  &:hover {
    opacity: 0.8;
  }
`;

const RiskRankingChip = styled.span<{ $level: string }>`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: white;

  ${props => {
    switch (props.$level) {
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

const GapTypeChip = styled.span`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #FEF9C3;
  color: #A16207;
`;

const GapOwnerText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1F2937;
`;

const ProcessOwnerContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #F8FAFC;
  border-radius: 50px;
`;

const ProcessOwnerAvatar = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #F97316;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 600;
`;

const ProcessOwnerName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1F2937;
`;

const CompensatingControlChip = styled.span`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ApplicationChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  background: white;
  color: #374151;
  border: 1px solid #E5E7EB;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    background: #F9FAFB;
  }
`;

const TruncatedText = styled.span<{ $maxWidth?: string }>`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${props => props.$maxWidth || '150px'};
  cursor: pointer;
  color: #2563EB;
  line-height: 1.4;
`;

const DateText = styled.span`
  font-size: 14px;
  color: #1F2937;
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SummaryLabel = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
`;

const SummaryValue = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #2563EB;
`;

// Tooltip styling
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #1F2937;
  margin-bottom: 8px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TooltipItem = styled.div`
  padding: 6px 10px;
  background: #EFF6FF;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: #2563EB;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;

  &:hover {
    background: #DBEAFE;
  }
`;

const SelectedObjectiveTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  margin: 2px;
`;

const ObjectiveProcessCode = styled.span`
  font-weight: 600;
  margin-right: 4px;
`;

const ObjectiveNumbersGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const ObjectiveNumberCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  background: transparent;
  color: #2563EB;
  border: 1px solid #D1D5DB;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
`;

const RelatedObjectsChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

// ==================== ACTION MENU COMPONENTS ====================

const ActionMenuButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

const MenuItemText = styled.span<{ $danger?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: ${props => props.$danger ? '#EF4444' : '#1F2937'};
`;

// ==================== DRAWER COMPONENTS ====================

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DrawerTextArea = styled.div`
  flex: 1;
  width: 100%;
  padding: 12px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #1F2937;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: #F9FAFB;
  overflow-y: auto;
`;

// ==================== MAIN COMPONENT ====================

// Location state interface for navigation
interface LocationState {
  processId?: string | null;
  processCode?: string | null;
  processName?: string | null;
  cycleId?: string | null;
  cycleCode?: string | null;
  cycleName?: string | null;
  status?: GapStatus | GapStatus[] | null;
  riskRating?: string | null;
  gapDateQuarter?: string[] | null;
  gapClosureDateQuarter?: string[] | null;
}

const GapsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  // Get filter params from navigation state
  const locationState = location.state as LocationState | null;
  const processId = locationState?.processId ?? null;
  const processCode = locationState?.processCode ?? null;
  const processName = locationState?.processName ?? null;
  const cycleId = locationState?.cycleId ?? null;
  const cycleCode = locationState?.cycleCode ?? null;
  const cycleName = locationState?.cycleName ?? null;
  const rawStatusFilter = locationState?.status ?? null;
  const statusFilter: GapStatus[] | null = rawStatusFilter
    ? (Array.isArray(rawStatusFilter) ? rawStatusFilter : [rawStatusFilter])
    : null;
  const riskRatingFilter = locationState?.riskRating ?? null;
  const gapDateQuarterNav = locationState?.gapDateQuarter ?? null;
  const gapClosureDateQuarterNav = locationState?.gapClosureDateQuarter ?? null;
  const hasNavigationParams = !!processId || !!cycleId || !!statusFilter || !!riskRatingFilter || !!gapDateQuarterNav || !!gapClosureDateQuarterNav;
  
  // Drawer states for viewing full text
  const [descriptionDrawerOpen, setDescriptionDrawerOpen] = useState(false);
  const [descriptionContent, setDescriptionContent] = useState('');
  const [descriptionTitle, setDescriptionTitle] = useState('');

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingGap, setDeletingGap] = useState<Gap | null>(null);

  // Change Log drawer state
  const [isDeleting, setIsDeleting] = useState(false);

  // Risk Matrix Modal state
  const [isRiskMatrixModalOpen, setIsRiskMatrixModalOpen] = useState(false);
  const [selectedGapForRiskUpdate, setSelectedGapForRiskUpdate] = useState<Gap | null>(null);

  // Archive Gap components
  const [archiveGapModalOpen, setArchiveGapModalOpen] = useState(false);
  const [gapToArchive, setGapToArchive] = useState<Gap | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);

  // Export panel state
  const [isExportPanelOpen, setIsExportPanelOpen] = useState(false);

  const { sortState, handleSort: baseHandleSort, sortedParam } = useTableSort({ defaultField: 'GapCode', defaultDirection: 'ascending' });

  // Sort loading state (only shows loading on sort icon, not full screen)
  const [isSortLoading, setIsSortLoading] = useState(false);
  // Ref to track previous sortedParam for detecting sort changes
  const prevSortedParamRef = useRef<string | null>(null);

  // Filter drawer state - single source of truth for all filters
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState<GapsFilterState>({status: ['Open']});
  // Filter loading state (shows spinner on filter button when applying)
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // Get hierarchy data from Redux (for looking up cycle/process info)
  const { cycles, processesByCycleId } = useSelector((state: RootState) => state.hierarchy);

  // console.log('statusFilter', statusFilter);

  // Sync navigation params to filters and reset navigation state
  // This runs once when navigating with params, builds filters, then clears navigation state
  useEffect(() => {
    // Default filter is Open status
    if (hasNavigationParams) {
      const newFilters: GapsFilterState = { };
      if (statusFilter) {
        newFilters.status = statusFilter;
      }

      // Map navigation riskRating to drawer filter
      if (riskRatingFilter) {
        newFilters.gapRiskRanking = [riskRatingFilter as RiskRanking];
      }
      
      // Map cycleId to drawer filter with full cycle info
      if (cycleId) {
        // Use cycle info from navigation state if available
        if (cycleCode && cycleName) {
          newFilters.cycles = [{
            cycleId: cycleId,
            cycleCode: cycleCode,
            cycleName: cycleName,
          }];
        } else {
          // Fallback: try to find from Redux
          const cycle = cycles.find(c => c.id === cycleId);
          if (cycle) {
            newFilters.cycles = [{
              cycleId: cycle.id,
              cycleCode: cycle.cycleCode,
              cycleName: cycle.cycleName,
            }];
          } else {
            // Last fallback: use cycleId as both id and code
            newFilters.cycles = [{
              cycleId: cycleId,
              cycleCode: cycleId,
              cycleName: cycleId,
            }];
          }
        }
      }
      
      // Map processId to drawer filter with full process info
      if (processId) {
        // Use process info from navigation state if available
        if (processCode && processName) {
          newFilters.processes = [{
            processId: processId,
            processCode: processCode,
            processName: processName,
            riskAssessmentId: '',
            processOwnerId: '',
            processOwnerName: '',
          }];
        } else if (cycleId) {
          // Fallback: try to find from Redux
          const processesForCycle = processesByCycleId[cycleId] || [];
          const process = processesForCycle.find(p => p.id === processId);
          if (process) {
            newFilters.processes = [{
              processId: process.id,
              processCode: process.processCode,
              processName: process.processName,
              riskAssessmentId: process.riskAssessmentId || '',
              processOwnerId: process.processOwnerId || '',
              processOwnerName: process.processOwner || '',
            }];
          } else {
            // Last fallback: use processId as both id and code
            newFilters.processes = [{
              processId: processId,
              processCode: processId,
              processName: processId,
              riskAssessmentId: '',
              processOwnerId: '',
              processOwnerName: '',
            }];
          }
        } else {
          // Last fallback: use processId as both id and code
          newFilters.processes = [{
            processId: processId,
            processCode: processId,
            processName: processId,
            riskAssessmentId: '',
            processOwnerId: '',
            processOwnerName: '',
          }];
        }
      }

      // Map quarter navigation params to filter
      if (gapDateQuarterNav) {
        newFilters.gapDateQuarter = gapDateQuarterNav as GapsFilterState['gapDateQuarter'];
      }
      if (gapClosureDateQuarterNav) {
        newFilters.gapClosureDateQuarter = gapClosureDateQuarterNav as GapsFilterState['gapClosureDateQuarter'];
      }

      setFilters(newFilters);
      // Reset navigation state immediately after building filters
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [processId, cycleId, statusFilter, riskRatingFilter, gapDateQuarterNav, gapClosureDateQuarterNav, cycles, processesByCycleId, navigate, location.pathname]);

  // Get current annual report from Redux
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { isAdmin, isExternalUser } = useSelector((state: RootState) => state.auth);

  // Get user data from Redux
  const { user } = useSelector((state: RootState) => state.auth);

  // Get gaps data from Redux
  const {
    gaps,
    totalCount,
    lastFetchedReportId,
    currentPage,
    pageSize
  } = useSelector((state: RootState) => state.gaps);

  // Sync local pageSize with global pageSize
  const { globalPageSize } = useSelector((state: RootState) => state.general);
  useEffect(() => {
    if (globalPageSize !== pageSize) {
      dispatch(setPageSizeAction(globalPageSize));
    }
  }, [globalPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Permission checks
  const isSuperAdmin = user?.userType === 'superAdministrator';
  const isInternalAdmin = user?.userType === 'internalAdministrators';
  const canDeleteAnyGap = isSuperAdmin; // Super Admin can delete any status
  const canDeleteDraftOnly = isInternalAdmin; // Internal Admin can only delete Draft

  // Wrap handleSort to set loading immediately when clicked
  const handleSort = useCallback((field: string) => {
    // Only set loading if we have data (not initial load)
    if (gaps.length > 0) {
      setIsSortLoading(true);
    }
    baseHandleSort(field);
  }, [baseHandleSort, gaps.length]);

  // Fetch gaps when report changes or filter/sort changes
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedReport?.annualReportId) return;

      // Skip fetch if navigation params exist - let navigation sync useEffect handle it first
      if (hasNavigationParams) {
        console.warn('⏳ Waiting for navigation params to sync...');
        return;
      }

      // Create current sort key
      const currentSortKey = sortedParam ? `${sortedParam[0]?.name}-${sortedParam[0]?.direction}` : 'default';
      
      // Check if this is initial load or data change (not sort-only)
      const isInitialOrDataChange = prevSortedParamRef.current === null || gaps.length === 0;
      
      // Update the ref with current sort key
      prevSortedParamRef.current = currentSortKey;
      
      // Set loading states - filter loading for filter changes, full loading for initial/data change
      if (isInitialOrDataChange) {
        setIsLoading(true);
      }
      setIsFilterLoading(true);
      
      try {
        // Build API request with drawer filters (single source of truth)
        const response = await getAllGaps({
          annualReportId: selectedReport.annualReportId,
          status: filters.status,
          cycleIds: filters.cycles?.map(c => c.cycleId),
          processIds: filters.processes?.map(p => p.processId),
          gapRiskRanking: filters.gapRiskRanking,
          gapType: filters.gapType,
          processOwnerIds: filters.processOwners?.map(o => o.userId),
          managementPrioritization: filters.managementPrioritization,
          dateIdentified: filters.dateIdentified ?? undefined,
          gapDateQuarter: filters.gapDateQuarter ?? undefined,
          gapClosureDateQuarter: filters.gapClosureDateQuarter ?? undefined,
          sorted: sortedParam,
        });

        dispatch(setGaps({
          data: response.data,
          totalCount: response.totalCount,
          reportId: selectedReport.annualReportId
        }));

        console.warn(`✅ Fetched ${response.data.length} gaps`);
      } catch (error) {
        console.error('Error fetching gaps:', error);
        message.error('Failed to load gaps');
      } finally {
        setIsLoading(false);
        setIsSortLoading(false);
        setIsFilterLoading(false);
      }
    };

    fetchData();
  }, [selectedReport, dispatch, sortedParam, filters]);

  // Check if any drawer filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.status?.length ||
      filters.cycles?.length ||
      filters.processes?.length ||
      filters.gapRiskRanking?.length ||
      filters.gapType?.length ||
      filters.processOwners?.length ||
      filters.managementPrioritization?.length ||
      filters.dateIdentified ||
      filters.gapDateQuarter ||
      filters.gapClosureDateQuarter
    );
  }, [filters]);

  // console.log('filters', filters);
  // console.log('has active filters', hasActiveFilters);

  // Filtered data comes directly from API (backend filtering)
  const filteredData = gaps;

  // Calculate paginated data from filtered data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, currentPage, pageSize]);

  const filteredTotalCount = hasActiveFilters ? filteredData.length : totalCount;
  const totalPages = Math.ceil(filteredTotalCount / pageSize);

  // Navigation handlers
  const handleNavigateToCycle = (cycleId: string) => {
    navigate('/process-hierarchy', { state: { selectedCycleId: cycleId } });
  };

  const handleNavigateToProcess = (processId: string) => {
    navigate('/process-hierarchy', { state: { selectedProcessId: processId } });
  };

  const handleNavigateToGap = (gap: Gap) => {
    if (!isExternalUser)
      navigate(`/gaps/${gap.id}`);
  };

  const handleNavigateToControl = (controlId: string) => {
    navigate(`/control-activities/${controlId}`);
  };

  const handleNavigateToApplication = (appId: string) => {
    navigate('/audit-plan', { state: { selectedApplicationId: appId } });
  };

  // Open description drawer
  const handleOpenDescription = (title: string, content: string) => {
    setDescriptionTitle(title);
    setDescriptionContent(content);
    setDescriptionDrawerOpen(true);
  };

  // Close description drawer
  const handleCloseDescription = () => {
    setDescriptionDrawerOpen(false);
    setDescriptionContent('');
    setDescriptionTitle('');
  };

  // Handle Archive button click - show confirmation modal
  const handleArchiveClick = (gap: Gap) => {
    setArchiveGapModalOpen(true);
    setGapToArchive(gap);
  };

  // Handle confirm Archive Gap from modal
  const handleConfirmArchive = () => {
    setArchiveGapModalOpen(false);
    handleArchive();
  };

  // Handle Archive (Open -> Archived)
    const handleArchive = async () => {
  
      setIsArchiving(true);
      //const formValues = form.getFieldsValue();
      if (gapToArchive == null)
        return;
      // Use today's date for gapClosureDate if not already set
      const closureDate = new Date().toISOString();
  
      const request: UpdateGapRequest = {
        gapId: gapToArchive.id,
        status: mapStatusToApi('Archived'),
        description: gapToArchive.gapDescription || '',
        riskDescription: gapToArchive.riskDescription || '',
        riskRating: mapRiskRankingToApi(gapToArchive.gapRiskRanking),
        riskRatingSeverityIndex: gapToArchive.riskRatingSeverityIndex ?? null,
        riskRatingLikelihoodIndex: gapToArchive.riskRatingLikelihoodIndex ?? null,
        type: mapGapTypeToApi(gapToArchive.gapType),
        owner: gapToArchive.gapOwner || '',
        processId: gapToArchive.linkedProcess.id,
        controlIds: gapToArchive.relatedControls.map(c => c.id),
        objectiveIds: gapToArchive.relatedObjectives.map(o => o.id),
        applicationIds: gapToArchive.applications.map(a => a.id),
        managementPrioritization: mapPriorityToApi(gapToArchive.managementPrioritization),
        remediationRecommendation: gapToArchive.remediationRecommendation || '',
        managementResponse: gapToArchive.managementResponse || '',
        dateIdentified: gapToArchive.dateIdentified || null,
        gapClosureDate: closureDate,
      };
  
      try {
        await updateGap(request);
        await reloadData();
        // Clear hierarchy cache so EX3 gap counts refresh
        dispatch(clearHierarchyData());
        message.success('Gap archived successfully');
      } catch (error: any) {
        console.error('Failed to archive gap:', error);
        message.error(error.message || 'Failed to archive gap');
      } finally {
        setIsArchiving(false);
      }
    };
  

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  // Define columns
  const columns: ColumnsType<Gap> = [
    {
      title: () => (
        <SortableColumnHeader
          title="GAP ID"
          field="GapCode"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'gapId',
      key: 'gapId',
      width: 100,
      fixed: 'left',
      render: (gapId: string, record) => (
        record.status === 'Draft' || !gapId ? (
          <GapIdLink onClick={() => handleNavigateToGap(record)}>
            Pending
          </GapIdLink>
        ) : (
          <GapIdLink onClick={() => handleNavigateToGap(record)}>
            {gapId}
          </GapIdLink>
        )
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Status"
          field="StatusRaw"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string, record) => (
        <StatusChip 
          $status={status} 
          onClick={() => handleNavigateToGap(record)}
          style={{ cursor: 'pointer' }}
        >
          {status}
        </StatusChip>
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Cycle"
          field="CycleCode"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'cycle',
      key: 'cycle',
      width: 140,
      render: (cycle: Gap['cycle']) => (
        <Tooltip
          title={
            <div>
              <TooltipTitle>Cycle</TooltipTitle>
              <TooltipList>
                <TooltipItem onClick={() => handleNavigateToCycle(cycle.id)}>
                  {cycle.code} {cycle.name}
                </TooltipItem>
              </TooltipList>
            </div>
          }
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          <CycleChip onClick={() => handleNavigateToCycle(cycle.id)}>
            {cycle.code} {cycle.name}
          </CycleChip>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Process"
          field="ProcessCode"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'linkedProcess',
      key: 'linkedProcess',
      width: 140,
      render: (process: Gap['linkedProcess']) => (
        <Tooltip
          title={
            <div>
              <TooltipTitle>Process</TooltipTitle>
              <TooltipList>
                <TooltipItem onClick={() => handleNavigateToProcess(process.id)}>
                  {process.code} {process.name}
                </TooltipItem>
              </TooltipList>
            </div>
          }
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          <ProcessChip onClick={() => handleNavigateToProcess(process.id)}>
            {process.code} {process.name}
          </ProcessChip>
        </Tooltip>
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Related Objectives"
          field="ObjectiveCodes"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'relatedObjectives',
      key: 'relatedObjectives',
      width: 150,
      render: (objectives: Gap['relatedObjectives']) => {
        if (!objectives || objectives.length === 0) {
          return <span style={{ color: '#9CA3AF' }}>-</span>;
        }

        const tooltipContent = objectives.length > 0 ? (
          <div>
            <TooltipTitle>Related Objectives</TooltipTitle>
            <TooltipList>
              {objectives.map((obj) => {
                // Extract process code from objective code (e.g., "A01.01" → "A01")
                const processCode = obj.code.split('.')[0];
                return (
                  <TooltipItem
                    key={obj.id}
                    onClick={() => navigate(`/risk-assessment/${processCode}/tab2`)}
                  >
                    {obj.code}
                  </TooltipItem>
                );
              })}
            </TooltipList>
          </div>
        ) : null;

        const chipContent = (
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
            {Object.entries(
                          objectives.reduce((acc, obj) => {
                            if (!acc[obj.processCode]) {
                              acc[obj.processCode] = [];
                            }
                            acc[obj.processCode].push(obj);
                            return acc;
                          }, {} as Record<string, GapObjective[]>)
                        ).map(([processCode, objectives]) => (
                          <SelectedObjectiveTag key={processCode}>
                            <ObjectiveProcessCode>{processCode}</ObjectiveProcessCode>
                            <ObjectiveNumbersGroup>
                              {objectives.map((obj) => (
                                <ObjectiveNumberCircle key={obj.id}>
                                  {obj.objectiveNumber}
                                </ObjectiveNumberCircle>
                              ))}
                            </ObjectiveNumbersGroup>
                          </SelectedObjectiveTag>
                        ))
                      }
          </div>
        );

        if (tooltipContent) {
          return (
            <Tooltip
              title={tooltipContent}
              placement="top"
              overlayStyle={{ maxWidth: '400px' }}
              overlayInnerStyle={{
                backgroundColor: '#FFFFFF',
                padding: '16px',
                borderRadius: '12px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              }}
              color="#FFFFFF"
            >
              {chipContent}
            </Tooltip>
          );
        }

        return chipContent;
      },
    },
    {
      title: 'Related Controls',
      dataIndex: 'relatedControls',
      key: 'relatedControls',
      width: 140,
      render: (controls: Gap['relatedControls']) => {
        if (!controls || controls.length === 0) {
          return <span style={{ color: '#9CA3AF' }}>-</span>;
        }

        const tooltipContent = controls.length > 0 ? (
          <div>
            <TooltipTitle>Related Controls</TooltipTitle>
            <TooltipList>
              {controls.map((con) => {
                // Extract process code from objective code (e.g., "A01.01" → "A01")
                //const processCode = con.code.split('.')[0];
                return (
                  <TooltipItem
                    key={con.id}
                    onClick={() => handleNavigateToControl(con.id)}
                  >
                    {con.code}
                  </TooltipItem>
                );
              })}
            </TooltipList>
          </div>
        ) : null;

        const chipContent = (
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
            {controls.map((con) => {
              return (
                <RelatedObjectsChip key={con.id} onClick={() => handleNavigateToControl(con.id)}>
                  {con.code}
                </RelatedObjectsChip>
              );
            })}
          </div>
        );

        if (tooltipContent) {
          return (
            <Tooltip
              title={tooltipContent}
              placement="top"
              overlayStyle={{ maxWidth: '400px' }}
              overlayInnerStyle={{
                backgroundColor: '#FFFFFF',
                padding: '16px',
                borderRadius: '12px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              }}
              color="#FFFFFF"
            >
              {chipContent}
            </Tooltip>
          );
        }

        return chipContent;
      },
    },
    {
      title: () => (
        <SortableColumnHeader
          title="GAP Description"
          field="Description"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'gapDescription',
      key: 'gapDescription',
      width: 140,
      render: (description: string) => (
        description ? (
          <TruncatedText
            $maxWidth="180px"
            onClick={() => handleOpenDescription('GAP Description', description)}
          >
            {description}
          </TruncatedText>
        ) : (
          <span style={{ color: '#9CA3AF' }}>-</span>
        )
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="GAP Risk Description"
          field="RiskDescription"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'riskDescription',
      key: 'riskDescription',
      width: 160,
      render: (riskDescription: string) => (
        riskDescription ? (
          <TruncatedText
            $maxWidth="180px"
            onClick={() => handleOpenDescription('GAP Risk Description', riskDescription)}
          >
            {riskDescription}
          </TruncatedText>
        ) : (
          <span style={{ color: '#9CA3AF' }}>-</span>
        )
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="GAP Risk Ranking"
          field="RiskRatingRaw"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'gapRiskRanking',
      key: 'gapRiskRanking',
      width: 150,
      render: (ranking: string, record) => (
        <RiskRankingChip 
          $level={ranking}
          onClick={() => handleOpenRiskMatrix(record)}
        >
          {ranking}
        </RiskRankingChip>
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="GAP Type"
          field="TypeRaw"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'gapType',
      key: 'gapType',
      width: 120,
      render: (type: string | null) => (
        type ? <GapTypeChip>{type}</GapTypeChip> : null
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="GAP Owner"
          field="Owner"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'gapOwner',
      key: 'gapOwner',
      width: 130,
      render: (owner: string) => (
        <GapOwnerText>{owner}</GapOwnerText>
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Process Owner"
          field="ProcessOwner"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'processOwner',
      key: 'processOwner',
      width: 150,
      render: (owner: Gap['processOwner']) => {
        if (!owner || !owner.name) {
          return <span style={{ color: '#9CA3AF' }}>Unknown</span>;
        }
        return (
          <ProcessOwnerContainer>
            <ProcessOwnerAvatar>
              {owner.avatar || owner.name.charAt(0)}
            </ProcessOwnerAvatar>
            <ProcessOwnerName>{owner.name}</ProcessOwnerName>
          </ProcessOwnerContainer>
        );
      },
    },
    {
      title: 'Compensating Controls',
      dataIndex: 'compensatingControls',
      key: 'compensatingControls',
      width: 160,
      render: (controls: Gap['compensatingControls']) => {
        if (!controls || controls.length === 0) {
          return <span style={{ color: '#9CA3AF' }}>-</span>;
        }
        
        const displayControl = controls[0];
        const tooltipContent = controls.length > 1 ? (
          <div>
            <TooltipTitle>Compensating Controls</TooltipTitle>
            <TooltipList>
              {controls.map((ctrl) => (
                <TooltipItem 
                  key={ctrl.id}
                  onClick={() => handleNavigateToControl(ctrl.id)}
                >
                  {ctrl.code}
                </TooltipItem>
              ))}
            </TooltipList>
          </div>
        ) : null;

        const chipContent = (
          <CompensatingControlChip onClick={() => handleNavigateToControl(displayControl.id)}>
            {displayControl.code}
          </CompensatingControlChip>
        );

        if (tooltipContent) {
          return (
            <Tooltip
              title={tooltipContent}
              placement="top"
              overlayStyle={{ maxWidth: '400px' }}
              overlayInnerStyle={{
                backgroundColor: '#FFFFFF',
                padding: '16px',
                borderRadius: '12px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              }}
              color="#FFFFFF"
            >
              {chipContent}
            </Tooltip>
          );
        }

        return chipContent;
      },
    },
    {
      title: 'Applications',
      dataIndex: 'applications',
      key: 'applications',
      width: 180,
      render: (applications: Gap['applications']) => {
        if (!applications || applications.length === 0) {
          return <span style={{ color: '#9CA3AF' }}>-</span>;
        }

        // Tooltip content showing all applications
        const tooltipContent = (
          <div>
            <TooltipTitle>Applications</TooltipTitle>
            <TooltipList>
              {applications.map((app) => (
                <TooltipItem
                  key={app.id}
                  onClick={() => handleNavigateToApplication(app.id)}
                >
                  {app.name}
                </TooltipItem>
              ))}
            </TooltipList>
          </div>
        );

        // Show all application chips in one row, cut off if too long
        const chipContent = (
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
            {applications.map((app) => (
              <ApplicationChip
                key={app.id}
                onClick={() => handleNavigateToApplication(app.id)}
              >
                {app.name}
              </ApplicationChip>
            ))}
          </div>
        );

        return (
          <Tooltip
            title={tooltipContent}
            placement="top"
            overlayStyle={{ maxWidth: '400px' }}
            overlayInnerStyle={{
              backgroundColor: '#FFFFFF',
              padding: '16px',
              borderRadius: '12px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            }}
            color="#FFFFFF"
          >
            {chipContent}
          </Tooltip>
        );
      },
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Management Prioritization"
          field="ManagementPrioritizationRaw"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'managementPrioritization',
      key: 'managementPrioritization',
      width: 200,
      onHeaderCell: () => ({ className: 'wrap-header' }),
      render: (priority: string | null) => (
        priority !== null ? (
          <RiskRankingChip $level={priority}>
            {priority}
          </RiskRankingChip>
        ) : (
          <span style={{ color: '#9CA3AF' }}>-</span>
        )
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Remediation Recommendation"
          field="RemediationRecommendation"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'remediationRecommendation',
      key: 'remediationRecommendation',
      width: 220,
      render: (text: string) => (
        text ? (
          <TruncatedText
            $maxWidth="180px"
            onClick={() => handleOpenDescription('Remediation Recommendation', text)}
          >
            {text}
          </TruncatedText>
        ) : (
          <span style={{ color: '#9CA3AF' }}>-</span>
        )
      ),
    },
    {
      title: "Management's Response",
      dataIndex: 'managementResponse',
      key: 'managementResponse',
      width: 180,
      render: (text: string) => (
        text ? (
          <TruncatedText
            $maxWidth="160px"
            onClick={() => handleOpenDescription("Management's Response", text)}
          >
            {text}
          </TruncatedText>
        ) : (
          <span style={{ color: '#9CA3AF' }}>-</span>
        )
      ),
    },
    {
      title: 'Date Identified',
      dataIndex: 'dateIdentified',
      key: 'dateIdentified',
      width: 110,
      render: (date: string) => (
        <DateText>{formatDate(date)}</DateText>
      ),
    },
    {
      title: 'GAP Closure Date',
      dataIndex: 'gapClosureDate',
      key: 'gapClosureDate',
      width: 130,
      render: (date: string | null, record) => (
        record.status === 'Archived' && date ? (
          <DateText>{formatDate(date)}</DateText>
        ) : (
          <span style={{ color: '#9CA3AF' }}>-</span>
        )
      ),
    },
    ...(isAdmin ? [
      {
        title: 'Action',
        key: 'actions',
        width: 80,
        fixed: 'right',
        align: 'center',
        render: (_: any, record: Gap) => {
          const menuItems: MenuProps['items'] = [
            {
              key: 'edit',
              label: (
                <MenuItemContent>
                  <EditIcon />
                  <MenuItemText>Edit</MenuItemText>
                </MenuItemContent>
              ),
              onClick: () => handleEditGap(record),
            },
            {
              key: 'archive',
              label: (
                <MenuItemContent>
                  <CheckOutlined />
                  <MenuItemText>Archive</MenuItemText>
                </MenuItemContent>
              ),
              onClick: () => handleArchiveClick(record),
              disabled: (() => {
                // Only an Open Gap can be closed/archived
                if (record.status == 'Open') return false;
                // All other cases: disabled
                return true;
              })(),
            },
            {
              type: 'divider',
            },
            {
              key: 'delete',
              label: (
                <MenuItemContent>
                  <TrashIcon />
                  <MenuItemText $danger>Delete</MenuItemText>
                </MenuItemContent>
              ),
              onClick: () => handleDeleteGap(record),
              // Permission: Super Admin can delete any, Internal Admin can only delete Draft
              // Process Owner / Others cannot delete at all
              disabled: (() => {
                // Super Admin can delete any status
                if (canDeleteAnyGap) return false;
                // Internal Admin can only delete Draft
                if (canDeleteDraftOnly && record.status === 'Draft') return false;
                // All other cases: disabled
                return true;
              })(),
            },
          ];

          const handleActionButtonClick = (e: React.MouseEvent) => {
            e.stopPropagation();
          };

          return (
            <div onClick={handleActionButtonClick}>
              <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                placement="bottomRight"
              >
                <ActionMenuButton >
                  <MoreOutlined />
                </ActionMenuButton>
              </Dropdown>
            </div>
          );
        },
      },
    ] : []),
  ] as ColumnsType<Gap>;

  // Handle page change
  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };

  // Handle Add Gap button click
  const handleAddGap = () => {
    navigate('/gaps/create');
  };

  // Handle Export button click
  const handleOpenExportPanel = () => {
    setIsExportPanelOpen(true);
  };

  const handleCloseExportPanel = () => {
    setIsExportPanelOpen(false);
  };

  // Calculate open gaps count
  const openGapsCount = useMemo(() => {
    return gaps.filter(gap => gap.status === 'Open' || gap.status === 'Archived').length;
  }, [gaps]);

  // Calculate open gaps count
  const closedGapsCount = useMemo(() => {
    return gaps.filter(gap => gap.status === 'Archived').length;
  }, [gaps]);

  // Prepare export data with flattened nested objects
  const exportData = useMemo(() => {
    return gaps.map(item => ({
      status: item.status,
      gapId: item.gapId || 'Pending',
      cycleCode: item.cycle?.code || '-',
      cycleName: item.cycle?.name || '-',
      processCode: item.linkedProcess?.code || '-',
      processName: item.linkedProcess?.name || '-',
      relatedObjectives: item.relatedObjectives?.length > 0 
        ? item.relatedObjectives.map(o => o.code).join(', ') 
        : '-',
      effectiveControl: item.effectiveControl?.code !== '-' 
        ? item.effectiveControl?.code 
        : '-',
      gapDescription: item.gapDescription || '-',
      riskDescription: item.riskDescription || '-',
      gapRiskRanking: item.gapRiskRanking || '-',
      gapType: item.gapType || '-',
      gapOwner: item.gapOwner || '-',
      processOwnerName: item.processOwner?.name || '-',
      relatedControls: item.relatedControls?.length > 0 
        ? item.relatedControls.map(c => c.code).join(', ') 
        : '-',
      applications: item.applications?.length > 0 
        ? item.applications.map(a => a.name).join(', ') 
        : '-',
      managementPrioritization: item.managementPrioritization || '-',
      remediationRecommendation: item.remediationRecommendation || '-',
      managementResponse: item.managementResponse || '-',
      dateIdentified: formatDate(item.dateIdentified),
      gapClosureDate: item.status === 'Archived' && item.gapClosureDate 
        ? formatDate(item.gapClosureDate) 
        : '-',
    }));
  }, [gaps]);

  // Handle opening Risk Matrix Modal
  const handleOpenRiskMatrix = (gap: Gap) => {
    setSelectedGapForRiskUpdate(gap);
    setIsRiskMatrixModalOpen(true);
  };

  // Handle Risk Matrix selection
  const handleRiskMatrixSelect = async (
    riskLevel: RiskRanking,
    severityIndex: number,
    likelihoodIndex: number
  ) => {
    if (!selectedGapForRiskUpdate) return;

    try {
      // Build update request with all required fields from existing gap
      const gap = selectedGapForRiskUpdate;
      const request: UpdateGapRequest = {
        gapId: gap.id,
        status: mapStatusToApi(gap.status),
        description: gap.gapDescription,
        riskDescription: gap.riskDescription,
        riskRating: mapRiskRankingToApi(riskLevel),
        riskRatingSeverityIndex: severityIndex,
        riskRatingLikelihoodIndex: likelihoodIndex,
        type: mapGapTypeToApi(gap.gapType),
        owner: gap.gapOwner,
        processId: gap.linkedProcess.id,
        controlIds: gap.compensatingControls.map(c => c.id),
        objectiveIds: gap.relatedObjectives.map(o => o.id),
        applicationIds: gap.applications.map(a => a.id),
        managementPrioritization: mapPriorityToApi(gap.managementPrioritization),
        remediationRecommendation: gap.remediationRecommendation,
        managementResponse: gap.managementResponse,
        dateIdentified: gap.dateIdentified,
        gapClosureDate: gap.gapClosureDate,
      };
      await updateGap(request);

      dispatch(clearHierarchyData());
      message.success(`Risk ranking updated to ${riskLevel}`);

      // Reload data to reflect changes
      await reloadData();
    } catch (error: any) {
      console.error('Failed to update risk ranking:', error);
      message.error(error.message || 'Failed to update risk ranking');
    } finally {
      // Close modal and reset state
      setIsRiskMatrixModalOpen(false);
      setSelectedGapForRiskUpdate(null);
    }
  };

  // Reload data - uses local filters only (navigation params are already mapped to filters)
  const reloadData = useCallback(async () => {
    if (!selectedReport?.annualReportId) return;

    try {
      const response = await getAllGaps({
        annualReportId: selectedReport.annualReportId,
        status: filters.status,
        cycleIds: filters.cycles?.map(c => c.cycleId),
        processIds: filters.processes?.map(p => p.processId),
        gapRiskRanking: filters.gapRiskRanking,
        gapType: filters.gapType,
        processOwnerIds: filters.processOwners?.map(o => o.userId),
        managementPrioritization: filters.managementPrioritization,
        dateIdentified: filters.dateIdentified ?? undefined,
      });
      dispatch(setGaps({
        data: response.data,
        totalCount: response.totalCount,
        reportId: selectedReport.annualReportId
      }));
    } catch (error) {
      console.error('Error refreshing gaps:', error);
    }
  }, [selectedReport, dispatch, filters]);

  // Action handlers
  const handleEditGap = (gap: Gap) => {
    handleNavigateToGap(gap);
  };

  const handleDeleteGap = (gap: Gap) => {
    setDeletingGap(gap);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingGap) {
      message.error('Gap ID is required for delete');
      return;
    }

    setIsDeleting(true);
    try {
      await deleteGap(deletingGap.id);
      await reloadData();
      // Clear hierarchy cache so EX3 gap counts refresh
      dispatch(clearHierarchyData());
      setDeleteModalOpen(false);
      setDeletingGap(null);
      message.success('Gap deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete gap:', error);
      message.error(error.message || 'Failed to delete Gap');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setDeletingGap(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>ICFR GAP Tracker</PageTitle>
        </PageHeader>
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading gaps...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>
      </PageContainer>
    );
  }

  // Empty state
  if (!isLoading && gaps.length === 0 && lastFetchedReportId !== null) {
    return (
      <PageContainer>
        <PageHeader>
          <PageHeaderRow>
          <PageTitle level={2}>ICFR GAP Tracker</PageTitle>
          <HeaderButtonGroup>
            <AddItemButton text="Add Gap" onClick={handleAddGap} disabled={!isAdmin} />
            
            {/* Filter drawer toggle button - at the end */}
            <FilterButton
              onClick={() => setFilterDrawerOpen(true)}
              hasActiveFilters={hasActiveFilters}
            />
          </HeaderButtonGroup>
        </PageHeaderRow>
        </PageHeader>
        <TableContainer>
          <EmptyStateContainer>
            {hasActiveFilters ? (
              <>
                <EmptyStateMessage>No results found for the current filters.</EmptyStateMessage>
                <AddItemButton 
                  text="Update Filters" 
                  onClick={() => setFilterDrawerOpen(true)} 
                />
              </>
            ) : (
              <>
                <EmptyStateMessage>There are no gaps to display yet.</EmptyStateMessage>
                {!isExternalUser ? <AddItemButton text="Add Gap" onClick={handleAddGap} /> : ""}
              </>
            )}
          </EmptyStateContainer>
        </TableContainer>
        
        {/* Keep filter drawer accessible in empty state */}
        <GapsFilterDrawer
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          onApply={(filters) => {
            setFilters(filters);
            dispatch(setCurrentPage(1));
            setFilterDrawerOpen(false);
          }}
          currentFilters={filters}
          annualReportId={selectedReport?.annualReportId || null}
        />
      </PageContainer>
    );
  }

  // Initial loading state
  if (gaps.length === 0 && lastFetchedReportId === null) {
    return (
      <PageContainer>
        <PageHeader>
          <PageHeaderRow>
          <PageTitle level={2}>ICFR GAP Tracker</PageTitle>
          <HeaderButtonGroup>
            <AddItemButton text="Add Gap" onClick={handleAddGap} disabled={!isAdmin} />
            
            {/* Filter drawer toggle button - at the end */}
            <FilterButton
              onClick={() => setFilterDrawerOpen(true)}
              hasActiveFilters={hasActiveFilters}
            />
          </HeaderButtonGroup>
        </PageHeaderRow>
        </PageHeader>
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading gaps...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderRow>
          <PageTitle level={2}>ICFR GAP Tracker</PageTitle>
          <HeaderButtonGroup>
            {!isExternalUser ? <AddItemButton text="Add Gap" onClick={handleAddGap} /> : ""}
            <ExportButton onClick={handleOpenExportPanel} title="Export" />
            {selectedReport && isAdmin && (
              <ChangeLogButton
                actionType="gap"
                annualReportId={selectedReport.annualReportId}
              />
            )}
            {/* Filter drawer toggle button - at the end */}
            <FilterButton
              onClick={() => setFilterDrawerOpen(true)}
              hasActiveFilters={hasActiveFilters}
            />
          </HeaderButtonGroup>
        </PageHeaderRow>
        <SummaryRow>
          <SummaryItem>
            <SummaryLabel>Opened GAPs:</SummaryLabel>
            <SummaryValue>{openGapsCount}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Closed GAPs:</SummaryLabel>
            <SummaryValue>{closedGapsCount}</SummaryValue>
          </SummaryItem>
        </SummaryRow>
      </PageHeader>

      <TableWrapper>
        {(isSortLoading || isFilterLoading) && (
          <LoadingOverlay>
            <LoadingOutlined style={{ fontSize: 32, color: '#3B82F6' }} spin />
          </LoadingOverlay>
        )}
        <TableContainer>
          <Table
            columns={!isExternalUser ? columns : columns.filter(col => col.title != 'Action')}
            dataSource={paginatedData}
            rowKey="id"
            pagination={false}
            scroll={{ x: 3360, y: 'calc(100vh - 300px)' }}
            size="middle"
          />

          <PaginationContainer>
            <Pagination
              current={currentPage}
              total={filteredTotalCount}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger
              pageSizeOptions={['10', '25', '50', '100']}
              onShowSizeChange={(_current, size) => dispatch(setGlobalPageSize(size))}
              showTotal={() => `${currentPage} of ${totalPages}`}
            />
          </PaginationContainer>
        </TableContainer>
      </TableWrapper>

      {/* Description Viewer Drawer */}
      <RightContentDrawer
        title={descriptionTitle}
        width={480}
        open={descriptionDrawerOpen}
        onClose={handleCloseDescription}
        maskClosable={true}
        footer={
          <ButtonGroup>
            <Button type="primary" onClick={handleCloseDescription}>
              Close
            </Button>
          </ButtonGroup>
        }
      >
        <DrawerContent>
          <DrawerTextArea>
            {descriptionContent}
          </DrawerTextArea>
        </DrawerContent>
      </RightContentDrawer>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        title={deletingGap?.status === 'Draft' ? 'Delete Draft Gap' : 'Warning: Delete Gap'}
        description={
          deletingGap ? (
            <div style={{ textAlign: 'left' }}>
              {deletingGap.status === 'Draft' ? (
                // Standard confirmation for Draft
                <>
                  <div style={{ marginBottom: '12px' }}>
                    Are you sure you want to delete draft gap?
                  </div>
                  <div style={{ color: '#6B7280' }}>
                    This action cannot be undone.
                  </div>
                </>
              ) : (
                // High-warning for Open/Archived (Super Admin only)
                <>
                  <div style={{ marginBottom: '12px', color: '#DC2626', fontWeight: 600 }}>
                    Warning: This will permanently remove the deficiency record.
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    Are you sure you want to delete gap <strong>{deletingGap.gapId}</strong>?
                  </div>
                  <div style={{ color: '#6B7280' }}>
                    This action cannot be undone. The GAP ID will be retired and cannot be reused.
                  </div>
                </>
              )}
            </div>
          ) : ''
        }
        icon={warningIcon}
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
        danger
      />

      {/* Risk Matrix Modal */}
      <RiskMatrixModal
        open={isRiskMatrixModalOpen}
        onClose={() => {
          setIsRiskMatrixModalOpen(false);
          setSelectedGapForRiskUpdate(null);
        }}
        onSelect={handleRiskMatrixSelect}
        selectedSeverityIndex={selectedGapForRiskUpdate?.riskRatingSeverityIndex}
        selectedLikelihoodIndex={selectedGapForRiskUpdate?.riskRatingLikelihoodIndex}
      />

      {/* Export Panel */}
      <ExportPanel
        open={isExportPanelOpen}
        onClose={handleCloseExportPanel}
        data={exportData}
        columns={[
          { key: 'status', title: 'Status' },
          { key: 'gapId', title: 'GAP ID' },
          { key: 'cycleCode', title: 'Cycle Code' },
          { key: 'cycleName', title: 'Cycle Name' },
          { key: 'processCode', title: 'Process Code' },
          { key: 'processName', title: 'Process Name' },
          { key: 'relatedObjectives', title: 'Related Objectives' },
          { key: 'relatedControls', title: 'Related Controls' },
          { key: 'gapDescription', title: 'GAP Description' },
          { key: 'riskDescription', title: 'GAP Risk Description' },
          { key: 'gapRiskRanking', title: 'GAP Risk Ranking' },
          { key: 'gapType', title: 'GAP Type' },
          { key: 'gapOwner', title: 'GAP Owner' },
          { key: 'processOwnerName', title: 'Process Owner' },
          { key: 'compensatingControls', title: 'Compensating Controls' },
          { key: 'applications', title: 'Applications' },
          { key: 'managementPrioritization', title: 'Management Prioritization' },
          { key: 'remediationRecommendation', title: 'Remediation Recommendation' },
          { key: 'managementResponse', title: "Management's Response" },
          { key: 'dateIdentified', title: 'Date Identified' },
          { key: 'gapClosureDate', title: 'GAP Closure Date' },
        ]}
        fileName="ICFR_GAP_Tracker_Report"
        title="ICFR GAP Tracker Report"
      />

      {/* Filter Drawer */}
      <GapsFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        onApply={(filters) => {
          setIsFilterLoading(true);
          setFilters(filters);
          dispatch(setCurrentPage(1)); // Reset to first page when filters change
          setFilterDrawerOpen(false); // Close drawer after applying
        }}
        currentFilters={filters}
        annualReportId={selectedReport?.annualReportId || null}
      />

      {/* Archive Gap Confirmation Modal */}
            <ConfirmModal
              open={archiveGapModalOpen}
              title="Archive this GAP?"
              description={
                <div style={{ textAlign: 'left' }}>
                  <div style={{ marginBottom: '12px' }}>
                    Are you sure you want to archive this GAP?
                  </div>
                  <div style={{ color: '#6B7280' }}>
                    The GAP Closure Date will be set to today. An archived GAP cannot be re-opened.
                  </div>
                </div>
              }
              confirmText="Archive GAP"
              cancelText="Cancel"
              loading={isArchiving}
              onConfirm={handleConfirmArchive}
              onClose={() => setArchiveGapModalOpen(false)}
            />
    </PageContainer>
  );
};

export default GapsPage;
